import { Box, Grid, Tooltip, useTheme } from "@mui/material";

import { Icon } from "@/modules/common/components/Icon";
import { Typography } from "@/modules/common/components/Typography";
import { PlayBox } from "@/modules/play/Components";
import { useCurrentStep, usePlay } from "@/modules/play/context/playContext";
import { getTeamActionsAtCurrentStep } from "@/modules/play/utils/teamActions";
import { ITeam } from "@/utils/types";

export { TeamConsoleHeader };

function TeamConsoleHeader({
  selectedTeamId,
  setSelectedTeamId,
}: {
  selectedTeamId: number;
  setSelectedTeamId: React.Dispatch<React.SetStateAction<number>>;
}) {
  const { teams } = usePlay();
  const currentStep = useCurrentStep();

  const isProductionStep = currentStep?.type === "production";
  const isSynthesisStep = currentStep?.id === "final-situation";

  return (
    <Grid container justifyContent="space-around" mt={2}>
      {teams.map((team) => {
        return (
          <Grid
            key={team.id}
            item
            onClick={() => setSelectedTeamId(team.id)}
            sx={{ cursor: "pointer" }}
            xs={2}
          >
            {isSynthesisStep ? (
              <TeamSynthesis
                team={team}
                isSelected={team.id === selectedTeamId}
              />
            ) : isProductionStep ? (
              <TeamProduction
                team={team}
                isSelected={team.id === selectedTeamId}
              />
            ) : (
              <TeamConsumption
                team={team}
                isSelected={team.id === selectedTeamId}
              />
            )}
          </Grid>
        );
      })}
    </Grid>
  );
}

function TeamProduction({
  team,
  isSelected,
}: {
  team: ITeam;
  isSelected: boolean;
}) {
  const { game, productionActionById } = usePlay();
  const theme = useTheme();

  const teamActionsAtCurrentStep = getTeamActionsAtCurrentStep(
    game.step,
    team.actions,
    productionActionById,
  );

  const teamActionCount = teamActionsAtCurrentStep.length;
  const touchedTeamActionsCount = teamActionsAtCurrentStep.filter(
    (teamAction) => teamAction.isTouched,
  ).length;
  const hasFinishedStep = teamActionCount === touchedTeamActionsCount;

  const borderColor = isSelected
    ? `${theme.palette.secondary.main} !important`
    : "white !important";
  const textColor =
    isSelected || hasFinishedStep
      ? `${theme.palette.secondary.main} !important`
      : "white !important";

  return (
    <PlayBox
      display="flex"
      flexDirection="column"
      borderColor={borderColor}
      color={textColor}
      gap={1}
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        gap={1}
        minHeight="24px"
      >
        {hasFinishedStep ? (
          <Icon name="check-circle" sx={{ width: 18 }} />
        ) : (
          <Icon name="mark-circle" sx={{ width: 18 }} />
        )}
        <Typography textAlign="center" variant="h5">
          {team.name}
        </Typography>
      </Box>

      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        gap={1}
        minHeight="24px"
      >
        <Icon name="team" />
        <Typography>
          {touchedTeamActionsCount}/{teamActionCount}
        </Typography>
      </Box>
    </PlayBox>
  );
}

function TeamConsumption({
  team,
  isSelected,
}: {
  team: ITeam;
  isSelected: boolean;
}) {
  const theme = useTheme();
  const { players } = usePlay();

  const playersInTeam = players.filter((p) => p.teamId === team.id);

  const color = isSelected
    ? `${theme.palette.secondary.main} !important`
    : "white !important";

  return (
    <PlayBox borderColor={color} color={color}>
      <Typography textAlign="center" variant="h5">
        {team.name}
      </Typography>

      <Box display="flex" minHeight="24px">
        {playersInTeam.map(({ user, hasFinishedStep }) => {
          return (
            <Tooltip key={user.id} title={`${user.firstName} ${user.lastName}`}>
              <div>
                {hasFinishedStep ? (
                  <Icon
                    name="player-finished"
                    sx={{ color: theme.palette.secondary.main }}
                  />
                ) : (
                  <Icon name="player" sx={{ color: "white" }} />
                )}
              </div>
            </Tooltip>
          );
        })}
      </Box>
    </PlayBox>
  );
}

function TeamSynthesis({
  team,
  isSelected,
}: {
  team: ITeam;
  isSelected: boolean;
}) {
  const theme = useTheme();

  const hasScenarioName = !!team.scenarioName;

  const borderColor = isSelected
    ? `${theme.palette.secondary.main} !important`
    : "white !important";
  const textColor =
    isSelected || hasScenarioName
      ? `${theme.palette.secondary.main} !important`
      : "white !important";

  return (
    <PlayBox
      display="flex"
      flexDirection="column"
      borderColor={borderColor}
      color={textColor}
      gap={1}
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        gap={1}
        minHeight="24px"
      >
        {hasScenarioName ? (
          <Icon name="check-circle" sx={{ width: 18 }} />
        ) : (
          <Icon name="mark-circle" sx={{ width: 18 }} />
        )}
        <Typography textAlign="center" variant="h5">
          {team.name}
        </Typography>
      </Box>

      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        gap={1}
        minHeight="24px"
      >
        <Icon name="team" />
        <Typography>{hasScenarioName ? 1 : 0}/1</Typography>
      </Box>
    </PlayBox>
  );
}
